import { NameBand, PLANT_NAME_SEARCH, RowGeometry, plantNameRect, searchPlantTitle } from './scanner/plantNameReader.ts';
import { ScannerRegion, StorageService } from './storageService.ts';
import {
  ScannerEvent,
  ScannerEventListener,
  ScannerDiagnostics,
  GeneRecognizer,
  ScanCandidate,
  ScannerRegionType
} from './scanner/scannerTypes.ts';
import { SCANNER_CONFIG } from './scanner/scannerConfig.ts';
import { GeneImagePreprocessor } from './scanner/GeneImagePreprocessor.ts';
import { TesseractGeneRecognizer } from './scanner/TesseractGeneRecognizer.ts';
import { RegionChangeDetector } from './scanner/RegionChangeDetector.ts';
import { FrameStabilityDetector } from './scanner/FrameStabilityDetector.ts';
import { TemporalVotingService } from './scanner/TemporalVotingService.ts';
import { PlantScanDeduplicator } from './scanner/PlantScanDeduplicator.ts';
import { ScannerStarvationDetector } from './scanner/ScannerStarvationDetector.ts';
import { AutoCalibrator, AutoCalibrateResult } from './scanner/AutoCalibrator.ts';
import { DesktopTemplateRecognizer, DesktopTemplateResult } from './scanner/DesktopTemplateRecognizer.ts';
import { rasterToCanvas, releaseRasterCanvases } from './scanner/vision/frameGrabber.ts';
import {
  CaptureFrameSource,
  createTrackFrameSource,
  createVideoFrameSource
} from './scanner/vision/captureFrameSource.ts';

export * from './scanner/scannerTypes.ts';
export * from './scanner/scannerConfig.ts';
export * from './scanner/AutoCalibrator.ts';
export * from './scanner/DesktopTemplateRecognizer.ts';
export * from './scanner/vision/badgeColumns.ts';
export * from './scanner/vision/desktopRowReader.ts';

/** Canvas pool slots reserved for the desktop path, clear of the camera path's. */
const SLOT_CANVAS_POOL_BASE = 10;

const GREEN_GENES = new Set(['G', 'Y', 'H']);
const RED_GENES = new Set(['W', 'X']);

/** How soon after an instant accept a one-slot-different read counts as its correction. */
const MISREAD_CORRECTION_WINDOW_MS = 2500;

function hammingDistance(a: string, b: string): number {
  if (a.length !== b.length) return Infinity;
  let d = 0;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) d++;
  return d;
}

export class ScannerService {
  private listeners: ScannerEventListener[] = [];
  private mediaStream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private frameSource: CaptureFrameSource | null = null;
  private isScanning = false;
  private isInitializing = false;

  // Background Web Worker ticker
  private tickerWorker: Worker | null = null;
  private scanTimerId: any = null;

  // Modular Pipeline Components
  private regions: ScannerRegion[] = [];
  private plantNameCanvas: HTMLCanvasElement | null = null;
  /** Plant names read while a row was being confirmed, keyed by region and genes. */
  private plantVotes = new Map<string, Map<string, number>>();
  private lastPlantRead: ScannerDiagnostics['plantRead'];
  private recognizer: GeneRecognizer;
  private changeDetector: RegionChangeDetector;
  private stabilityDetector: FrameStabilityDetector;
  private votingService: TemporalVotingService;
  private deduplicator: PlantScanDeduplicator;
  private starvationDetector: ScannerStarvationDetector;

  // Performance & Diagnostics Tracking
  private lastPreviewEmitTime = 0;
  private isOcrInProgress = false;
  private lastOcrTimestamps: Record<number, number> = {};
  private scanCount = 0;
  private acceptedCount = 0;
  /** The last row each region emitted, to recognise a quick re-read of the same tooltip. */
  private lastEmits: Record<number, { genes: string; at: number; instant: boolean }> = {};
  private rejectedCount = 0;
  private frameCount = 0;
  private lastFpsCalcTime = Date.now();
  private currentFps = 0;
  private lastScanLatency = 0;
  private lastOcrLatency = 0;
  private lastRowOcrLatency = 0;
  private lastSlotOcrLatency = 0;
  private lastTickTime = 0;
  private lastTickGap = 0;
  private lastVideoFrameTime = 0;
  private lastVideoFrameGap = 0;
  private pipelineStage = 'idle';
  private pipelineStageStartedAt = performance.now();
  private pendingUiGene = '';
  private pendingUiStartedAt = 0;
  private lastUiUpdateLatency = 0;
  private latestConfidence = 0;
  private activityScores: Record<number, number> = { 0: 0, 1: 0 };
  private activeRegionType: ScannerRegionType | 'none' = 'none';
  // Preview rendering is UI-only work. It runs independently of recognition so the OCR
  // pipeline keeps going while the app is in the background, but there is no point paying
  // for it when nobody is looking at the calibration/preview panel.
  private previewEnabled = true;

  // Most recent per-region read, kept so diagnostics can name the slot that failed rather
  // than reporting a bare "no result".
  private lastReads: Record<number, DesktopTemplateResult | null> = {};
  // One second-opinion attempt per stuck tooltip, not one per frame.
  private slotOcrAttempts: Record<number, { signature: number; at: number }> = {};
  private slotOcrInFlight = false;
  private fallbackReadCount = 0;
  private lastScannedFrameCount = -1;

  // Reusable Canvases
  private previewCanvases: HTMLCanvasElement[] = [];
  private roiCanvases: HTMLCanvasElement[] = [];

  constructor(recognizer?: GeneRecognizer) {
    this.regions = StorageService.getScannerRegions();
    this.recognizer = recognizer || new TesseractGeneRecognizer();
    this.changeDetector = new RegionChangeDetector();
    this.stabilityDetector = new FrameStabilityDetector();
    this.votingService = new TemporalVotingService();
    this.deduplicator = new PlantScanDeduplicator();
    this.starvationDetector = new ScannerStarvationDetector();
  }

  public static isSupported(): boolean {
    return typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      'getDisplayMedia' in navigator.mediaDevices;
  }

  /**
   * Enables/disables preview rendering. The calibration panel turns this on while mounted
   * and off when it unmounts, so the scanner does no preview work during normal scanning
   * when the panel isn't on screen. Recognition is never gated by this.
   */
  public setPreviewEnabled(enabled: boolean): void {
    this.previewEnabled = enabled;
  }

  public addEventListener(listener: ScannerEventListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private emit(event: ScannerEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }

  public async start(): Promise<boolean> {
    if (this.isScanning || this.isInitializing) return false;
    this.isInitializing = true;

    // Start the OCR warmup here so its first-run asset download (~14MB) overlaps the screen
    // picker and video setup below instead of starting once the user has already chosen a
    // window. Both engines are warmed: the single-character one is what completes a slot the
    // template matcher gave up on, and creating it on first use would mean the very read
    // that needs help most is also the one that waits seconds for a worker to spawn.
    if (!this.recognizer.isWarm()) {
      void this.recognizer.warmup().catch(() => {});
    }
    void this.recognizer.warmupSlotWorker?.().catch(() => {});

    try {
      this.regions = StorageService.getScannerRegions();

      this.mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: SCANNER_CONFIG.capture.idealWidth },
          height: { ideal: SCANNER_CONFIG.capture.idealHeight },
          frameRate: { max: SCANNER_CONFIG.capture.maxFrameRate }
        },
        audio: false
      });

      this.emit({ type: 'INITIALIZING' });

      const videoTrack = this.mediaStream.getVideoTracks()[0];
      if (!videoTrack) {
        throw new Error('No video track received from screen capture');
      }

      videoTrack.addEventListener('ended', () => {
        this.stop();
      });

      // Take frames off the capture track directly where the platform allows it, so
      // recognition never depends on the renderer presenting them. See
      // `captureFrameSource.ts` for why that is the difference behind a fullscreen game.
      this.frameSource = createTrackFrameSource(videoTrack);

      if (!this.frameSource) {
        // Mount the video element in the DOM so Chromium does not throttle frame decoding.
        // Only built on this path: when the track processor works, an element attached to
        // the same track would be a second sink doing precisely the decode-and-present work
        // the track path exists to skip.
        this.videoElement = document.createElement('video');
        this.videoElement.autoplay = true;
        this.videoElement.playsInline = true;
        this.videoElement.muted = true;
        this.videoElement.style.position = 'fixed';
        this.videoElement.style.top = '-9999px';
        this.videoElement.style.left = '-9999px';
        this.videoElement.style.width = '100px';
        this.videoElement.style.height = '100px';
        this.videoElement.style.opacity = '0.001';
        this.videoElement.style.pointerEvents = 'none';
        this.videoElement.style.zIndex = '-9999';
        document.body.appendChild(this.videoElement);

        const video = this.videoElement;

        // Robust video initialization that never hangs on readyState
        await new Promise<void>((resolve) => {
          let isDone = false;
          const done = () => {
            if (isDone) return;
            isDone = true;
            video.play().then(() => resolve()).catch(() => resolve());
          };

          video.onloadedmetadata = done;
          video.onloadeddata = done;
          video.oncanplay = done;

          video.srcObject = this.mediaStream;

          if (video.readyState >= 1 && video.videoWidth > 0) {
            done();
          }

          // Safety fallback timeout
          setTimeout(done, 1200);
        });

        this.frameSource = createVideoFrameSource(video);
      }

      this.isScanning = true;
      this.isInitializing = false;
      this.starvationDetector.start();
      this.emit({ type: 'STARTED' });

      this.startScanLoop();
      return true;
    } catch (err: any) {
      this.stop();
      this.isInitializing = false;
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        this.emit({ type: 'ERROR', error: err?.message || 'Failed to initialize screen capture' });
      }
      this.emit({ type: 'STOPPED' });
      return false;
    }
  }


  private lastScanExecuteTime = 0;

  private startScanLoop(): void {
    if (!this.isScanning) return;

    // Scan when the capture produces a frame. Reading the same pixels twice cannot yield a
    // different answer, so anything faster than the capture rate is wasted work, and
    // anything driven by a timer instead risks reading a frame that never changed.
    this.frameSource?.onFrame(() => {
      if (!this.isScanning) return;
      const now = performance.now();
      if (now - this.lastScanExecuteTime < 15) return;
      this.lastScanExecuteTime = now;
      this.scanFrame();
    });

    try {
      const tickerBlob = new Blob([`
        let timer = null;
        self.onmessage = function(e) {
          if (e.data === 'START') {
            if (timer) clearInterval(timer);
            timer = setInterval(function() {
              self.postMessage('TICK');
            }, ${SCANNER_CONFIG.performance.scanIntervalMs});
          } else if (e.data === 'STOP') {
            if (timer) clearInterval(timer);
            timer = null;
          }
        };
      `], { type: 'application/javascript' });

      this.tickerWorker = new Worker(URL.createObjectURL(tickerBlob));
      this.tickerWorker.onmessage = () => {
        if (this.isScanning) {
          const now = performance.now();
          if (now - this.lastScanExecuteTime >= SCANNER_CONFIG.performance.scanIntervalMs - 3) {
            this.lastScanExecuteTime = now;
            this.scanFrame();
          }
        }
      };
      this.tickerWorker.postMessage('START');
    } catch {
      const runScan = async () => {
        if (!this.isScanning) return;
        await this.scanFrame();
        if (this.isScanning) {
          this.scanTimerId = setTimeout(runScan, SCANNER_CONFIG.performance.scanIntervalMs);
        }
      };
      runScan();
    }
  }

  private async scanFrame(): Promise<void> {
    const source = this.frameSource;
    const frame = source?.current();
    if (!source || !frame || source.width === 0) return;

    const startTime = performance.now();
    if (this.lastTickTime > 0) {
      this.lastTickGap = startTime - this.lastTickTime;
    }
    this.lastTickTime = startTime;

    // Frame health now comes from the capture itself rather than from the tick clock.
    //
    // These metrics used to be stamped on every tick where the video was merely decodable,
    // which made "capture stalled" and "tick delayed" two names for one measurement: the
    // frame numbers could not go bad unless the ticker had already gone bad, and a capture
    // that had genuinely frozen while ticks kept arriving looked perfectly healthy. The
    // distinction is the whole point of the advice the HUD gives -- capping in-game FPS
    // helps a starved capture and does nothing for a throttled timer.
    this.lastVideoFrameTime = source.lastFrameAt;
    this.lastVideoFrameGap = source.lastFrameGapMs;

    // The ticker keeps calling this even when the capture has stopped delivering, which is
    // what lets starvation be noticed and reported at all. Re-reading pixels that have not
    // changed cannot produce a different answer, though, so everything below the metrics is
    // skipped until a new frame actually arrives.
    const isNewFrame = source.frameCount !== this.lastScannedFrameCount;
    this.lastScannedFrameCount = source.frameCount;

    if (!isNewFrame) {
      this.lastScanLatency = performance.now() - startTime;
      this.evaluateStarvation(startTime);
      return;
    }

    if (!this.isOcrInProgress) {
      this.setPipelineStage('capture');
    }

    const videoW = source.width;
    const videoH = source.height;
    const now = Date.now();

    // FPS Calculation
    this.frameCount++;
    if (now - this.lastFpsCalcTime >= 1000) {
      this.currentFps = Math.round((this.frameCount * 1000) / (now - this.lastFpsCalcTime));
      this.frameCount = 0;
      this.lastFpsCalcTime = now;
    }

    // Preview is emitted whenever previewEnabled is true, independent of document.visibilityState,
    // so background windows, overlays, and multi-monitor setups receive real-time preview streams.
    const shouldEmitPreview =
      this.previewEnabled && now - this.lastPreviewEmitTime >= SCANNER_CONFIG.performance.previewIntervalMs;
    if (shouldEmitPreview) {
      this.lastPreviewEmitTime = now;
    }

    // Step 1: Evaluate Active Regions and Previews
    const activeCandidates: {
      rIdx: number;
      type: ScannerRegionType;
      xPx: number;
      yPx: number;
      wPx: number;
      hPx: number;
      geneWPx: number;
      gapWPx: number;
      signature: number;
      activityScore: number;
      roiData?: Uint8ClampedArray;
    }[] = [];

    for (let rIdx = 0; rIdx < this.regions.length; rIdx++) {
      const reg = this.regions[rIdx];
      const xPx = Math.round(videoW * reg.TOP_LEFT_X);
      const yPx = Math.round(videoH * reg.TOP_LEFT_Y);
      const wPx = Math.round(videoW * reg.WIDTH);
      const normH = reg.WIDTH * reg.HEIGHT_TO_WIDTH_RATIO;
      const hPx = Math.ceil(videoH * normH);

      if (wPx <= 0 || hPx <= 0) continue;

      // Rust Breeder-style Preview Rendering (throttled)
      if (shouldEmitPreview) {
        this.renderPreview(rIdx, xPx, yPx, wPx, hPx, reg, frame);
      }

      if (!this.roiCanvases[rIdx]) {
        this.roiCanvases[rIdx] = document.createElement('canvas');
      }
      const roiCanvas = this.roiCanvases[rIdx];
      roiCanvas.width = wPx;
      roiCanvas.height = hPx;
      const roiCtx = roiCanvas.getContext('2d', { willReadFrequently: true });
      if (!roiCtx) continue;

      roiCtx.drawImage(frame, xPx, yPx, wPx, hPx, 0, 0, wPx, hPx);
      const roiData = roiCtx.getImageData(0, 0, wPx, hPx).data;

      // Activity Score Calculation
      const score = GeneImagePreprocessor.computeRegionActivityScore(roiData);
      this.activityScores[rIdx] = score;

      if (score < SCANNER_CONFIG.recognition.activeRegionThreshold) {
        const wasVisible = this.deduplicator.isRegionCurrentlyVisible(rIdx);
        this.deduplicator.markRegionDismissed(rIdx);
        if (wasVisible) {
          this.emit({ type: 'IDLE', regionIndex: rIdx });
        }
        continue;
      }

      const signature = this.changeDetector.computeSignature(roiCtx, wPx, hPx);
      const hasChanged = this.changeDetector.hasChanged(rIdx, signature);
      // Fast-Path: when tooltip activity is present, use fastStableDurationMs (15ms)
      const isStable = this.stabilityDetector.registerFrame(
        rIdx,
        signature,
        SCANNER_CONFIG.performance.roiChangeThreshold,
        SCANNER_CONFIG.performance.fastStableDurationMs
      );
      const lastOcr = this.lastOcrTimestamps[rIdx] || 0;

      if ((hasChanged && isStable) || isStable || now - lastOcr > 120) {
        const geneWPx = Math.round(wPx * reg.GENE_WIDTH_TO_WIDTH_RATIO);
        const totalGeneW = geneWPx * 6;
        const gapWPx = Math.max(0, (wPx - totalGeneW) / 5);

        activeCandidates.push({
          rIdx,
          type: rIdx === 0 ? 'inventory' : 'planter',
          xPx,
          yPx,
          wPx,
          hPx,
          geneWPx,
          gapWPx,
          signature,
          activityScore: score,
          roiData
        });
      }
    }

    if (activeCandidates.length === 0 && this.activeRegionType !== 'none') {
      this.activeRegionType = 'none';
      this.emit({
        type: 'ACTIVE_REGION_CHANGED',
        activeRegion: null,
        regionIndex: undefined
      });
    }

    // Step 2: Arbitrated Multi-Region Recognition
    // Fast-path template recognition does not even need Tesseract to be warm!
    if (activeCandidates.length > 0 && !this.isOcrInProgress) {
      this.isOcrInProgress = true;
      this.scanCount++;

      this.processArbitratedScan(activeCandidates)
        .catch((error) => {
          console.error('Scanner pipeline failed', error);
          this.setPipelineStage('error');
        })
        .finally(() => {
          this.isOcrInProgress = false;
        });
    } else if (!this.isOcrInProgress) {
      this.setPipelineStage(
        activeCandidates.length === 0
          ? 'roi-idle'
          : 'ocr-cold'
      );
    }

    this.lastScanLatency = performance.now() - startTime;
    this.evaluateStarvation(startTime);
  }

  /** Watches capture and pipeline health, and announces changes to it. */
  private evaluateStarvation(startTime: number): void {
    const starvationEval = this.starvationDetector.evaluate({
      videoFrameAgeMs: this.lastVideoFrameTime > 0 ? startTime - this.lastVideoFrameTime : 0,
      videoFrameGapMs: this.lastVideoFrameGap,
      lastOcrLatencyMs: this.lastOcrLatency,
      rowOcrLatencyMs: this.lastRowOcrLatency,
      tickGapMs: this.lastTickGap,
      pipelineStage: this.pipelineStage,
      pipelineStageAgeMs: startTime - this.pipelineStageStartedAt
    });

    if (!starvationEval.stateChanged) return;

    if (starvationEval.isStarved) {
      this.emit({
        type: 'STARVATION_DETECTED',
        isStarved: true,
        starvationReason: starvationEval.starvationReason,
        diagnostics: this.getDiagnostics()
      });
    } else {
      this.emit({
        type: 'STARVATION_RESOLVED',
        isStarved: false,
        diagnostics: this.getDiagnostics()
      });
    }
  }

  /**
   * Asks the OCR engine about the slots template matching could not name.
   *
   * The two recognisers fail in unrelated ways. Template matching compares against shapes
   * drawn from stroke geometry, so it is fast and always produces six answers, but it can be
   * defeated by a typeface detail it has no reference for. Tesseract has actually seen a
   * great many typefaces and is slow. Running the slow one on one or two slots, only when
   * the fast one has already given up, costs nothing in the normal case and is the
   * difference between a plant being scanned and being silently skipped.
   *
   * Whatever comes back still has to survive the badge colour: a red badge holds W or X, and
   * an OCR letter that disagrees is discarded rather than trusted over the colour.
   */
  private async completeWithSlotOcr(
    item: { rIdx: number; wPx: number; hPx: number; geneWPx: number; gapWPx: number; signature: number; roiData?: Uint8ClampedArray },
    read: DesktopTemplateResult
  ): Promise<{ geneString: string; confidence: number; latencyMs: number } | null> {
    const readSlotLetters = this.recognizer.readSlotLetters;
    if (!readSlotLetters || !item.roiData) return null;

    // One unreadable slot is a typeface problem worth a second opinion. Three is a
    // misaligned region, and guessing half a row is how a wrong clone gets saved.
    if (read.resolvedCount < 4 || read.unresolvedSlots.length === 0) return null;
    if (this.slotOcrInFlight) return null;

    const previous = this.slotOcrAttempts[item.rIdx];
    const now = Date.now();
    const sameTooltip =
      previous && Math.abs(item.signature - previous.signature) / Math.max(1, Math.abs(previous.signature)) < 0.02;
    if (sameTooltip && now - previous.at < SCANNER_CONFIG.recognition.slotOcrRetryMs) return null;

    this.slotOcrAttempts[item.rIdx] = { signature: item.signature, at: now };
    this.slotOcrInFlight = true;
    this.setPipelineStage('slot-ocr');
    const started = performance.now();

    try {
      // Re-read with the rasters attached. The first pass skips them because allocating six
      // images a frame to throw away is a waste; this path runs rarely enough to afford it.
      const detailed = DesktopTemplateRecognizer.recognizeFromRoi(
        item.roiData,
        item.wPx,
        item.hPx,
        item.geneWPx,
        item.gapWPx,
        true
      );
      if (!detailed) return null;

      const canvases: HTMLCanvasElement[] = [];
      const indices: number[] = [];
      for (const index of detailed.unresolvedSlots) {
        const image = detailed.slots[index]?.image;
        if (!image) continue;
        const canvas = rasterToCanvas(image, SLOT_CANVAS_POOL_BASE + index);
        if (!canvas) continue;
        canvases.push(canvas);
        indices.push(index);
      }

      if (canvases.length !== detailed.unresolvedSlots.length) return null;

      const letters = await readSlotLetters.call(this.recognizer, canvases);
      const resolved = detailed.slots.map(slot => (slot.reject === null ? slot.gene : null));

      for (let i = 0; i < indices.length; i++) {
        const index = indices[i];
        const letter = letters[i];
        if (!letter) return null;

        const color = detailed.slots[index].color;
        if (color === 'green' && !GREEN_GENES.has(letter)) return null;
        if (color === 'red' && !RED_GENES.has(letter)) return null;

        resolved[index] = letter as typeof resolved[number];
      }

      if (resolved.some(letter => !letter)) return null;

      this.fallbackReadCount++;
      const geneString = resolved.join('');
      console.log(`[Scanner SlotOCR] Completed ${detailed.partial} -> ${geneString}`);

      return {
        geneString,
        // Deliberately below a clean template read. The row is trustworthy enough to emit and
        // the user should still be able to tell the two apart in the HUD.
        confidence: Math.max(SCANNER_CONFIG.recognition.minConfidence, Math.round(read.confidence * 0.9)),
        latencyMs: performance.now() - started
      };
    } catch (error) {
      console.warn('[Scanner SlotOCR] second opinion failed', error);
      return null;
    } finally {
      this.slotOcrInFlight = false;
    }
  }

  private async processArbitratedScan(
    regionsToScan: {
      rIdx: number;
      type: ScannerRegionType;
      xPx: number;
      yPx: number;
      wPx: number;
      hPx: number;
      geneWPx: number;
      gapWPx: number;
      signature: number;
      activityScore: number;
      roiData?: Uint8ClampedArray;
    }[]
  ): Promise<void> {
    if (!this.frameSource) return;
    const ocrStartTime = performance.now();
    const candidates: ScanCandidate[] = [];
    let rowOcrLatency = 0;
    let slotOcrLatency = 0;

    for (const item of regionsToScan) {
      this.lastOcrTimestamps[item.rIdx] = Date.now();
      if (!item.roiData) continue;

      this.setPipelineStage('template-match');
      const read = DesktopTemplateRecognizer.recognizeFromRoi(
        item.roiData,
        item.wPx,
        item.hPx,
        item.geneWPx,
        item.gapWPx
      );

      if (!read) {
        this.lastReads[item.rIdx] = null;
        continue;
      }

      this.lastReads[item.rIdx] = read;
      rowOcrLatency += read.latencyMs;

      let geneString = read.geneString;
      let confidence = read.confidence;
      let instantAccept =
        read.minSlotConfidence >= SCANNER_CONFIG.recognition.instantAcceptSlotConfidence &&
        read.layoutSource !== 'calibration';

      if (!read.success) {
        // The row is real -- the badges are there -- but a slot would not name itself. On a
        // still tooltip the next frame is the same pixels, so re-running the same classifier
        // will fail the same way forever. Asking a different engine about just that slot is
        // the only thing that changes the outcome, and it is cheap because it happens once
        // per stuck tooltip rather than once per frame.
        const completed = await this.completeWithSlotOcr(item, read);
        if (!completed) continue;
        geneString = completed.geneString;
        confidence = completed.confidence;
        // Two independent recognisers named the row between them. That is a stronger
        // agreement than three frames of one recogniser, so it does not wait for the window.
        instantAccept = true;
        slotOcrLatency += completed.latencyMs;
      }

      if (!geneString || confidence < SCANNER_CONFIG.recognition.minConfidence) continue;

      const centers = read.geometry?.centers;
      candidates.push({
        regionIndex: item.rIdx,
        regionType: item.type,
        genes: geneString,
        confidence,
        geneConfidences: read.slotConfidences,
        activityScore: item.activityScore,
        valid: true,
        acceptImmediately: instantAccept,
        // The ROI is drawn 1:1 from the frame at (xPx, yPx), so frame = ROI + offset.
        rowGeometry:
          centers && centers.length >= 2 && read.geometry
            ? {
                x: item.xPx + centers[0],
                y: item.yPx + read.geometry.centerY,
                pitch: (centers[centers.length - 1] - centers[0]) / (centers.length - 1)
              }
            : undefined
      });
    }

    this.lastOcrLatency = performance.now() - ocrStartTime;
    this.lastRowOcrLatency = rowOcrLatency;
    this.lastSlotOcrLatency = slotOcrLatency;
    this.setPipelineStage('vote');

    if (candidates.length === 0) {
      this.setPipelineStage('no-result');
      if (this.activeRegionType !== 'none') {
        this.activeRegionType = 'none';
        this.emit({
          type: 'ACTIVE_REGION_CHANGED',
          activeRegion: null,
          regionIndex: undefined
        });
      }
      return;
    }

    // Step 2b: Identify which region actually detected real gene slots
    const detectedCandidate = candidates.slice().sort((a, b) => b.confidence - a.confidence)[0];
    const detectedRegionType: 'none' | ScannerRegionType = detectedCandidate.regionType;
    const detectedRegionIndex = detectedCandidate.regionIndex;

    if (detectedRegionType !== this.activeRegionType) {
      this.activeRegionType = detectedRegionType;
      this.emit({
        type: 'ACTIVE_REGION_CHANGED',
        activeRegion: detectedRegionType,
        regionIndex: detectedRegionIndex
      });
    }

    // Step 3: Confirm and emit each region independently.
    let emittedAny = false;
    let confirmedAny = false;
    const emittedGenesThisCycle = new Set<string>();

    for (const candidate of candidates) {
      this.latestConfidence = candidate.confidence;
      this.votePlantType(candidate);

      // Step 4: Temporal confirmation (per-region history with instant fast-path)
      const votedResult = this.votingService.addCandidate(
        candidate.regionIndex,
        {
          geneString: candidate.genes,
          confidence: candidate.confidence
        },
        candidate.acceptImmediately
      );

      if (!votedResult) continue;
      confirmedAny = true;

      // Guard: the same genotype captured by two overlapping regions in one cycle
      // (e.g. a single tooltip) should only be emitted once.
      if (emittedGenesThisCycle.has(votedResult.geneString)) continue;

      // Step 5: Display-state lock (per-region dedup)
      const targetRegion = regionsToScan.find(r => r.rIdx === candidate.regionIndex);
      const signature = targetRegion ? targetRegion.signature : 0;

      // A row accepted instantly (one confident frame, no vote) can carry one misread slot;
      // the next frames then settle on the true genes, which differ from it in that one slot.
      // That is the same tooltip read better, not a second plant, so it replaces the first.
      const previousEmit = this.lastEmits[candidate.regionIndex];
      const emitNow = performance.now();
      const isCorrection =
        !!previousEmit &&
        previousEmit.instant &&
        this.deduplicator.isRegionCurrentlyVisible(candidate.regionIndex) &&
        emitNow - previousEmit.at < MISREAD_CORRECTION_WINDOW_MS &&
        hammingDistance(previousEmit.genes, votedResult.geneString) === 1;

      const shouldEmit = this.deduplicator.shouldAccept(
        candidate.regionIndex,
        votedResult.geneString,
        signature
      );

      if (!shouldEmit) {
        continue;
      }

      // A correction is never itself corrected, so two readings cannot flip back and forth.
      this.lastEmits[candidate.regionIndex] = {
        genes: votedResult.geneString,
        at: emitNow,
        instant: !!candidate.acceptImmediately && !isCorrection
      };

      if (isCorrection) {
        console.log(`[Scanner EMIT] >>> SAPLING-CORRECTED: ${previousEmit.genes} -> ${votedResult.geneString}`);
        emittedGenesThisCycle.add(votedResult.geneString);
        emittedAny = true;
        this.emit({
          type: 'SAPLING-CORRECTED',
          regionIndex: candidate.regionIndex,
          regionType: candidate.regionType,
          geneString: votedResult.geneString,
          previousGeneString: previousEmit.genes,
          confidence: votedResult.confidence
        });
        continue;
      }

      console.log(`[Scanner EMIT] >>> SAPLING-FOUND: ${votedResult.geneString} (conf: ${Math.round(votedResult.confidence)}%)`);

      this.acceptedCount++;
      this.pendingUiGene = votedResult.geneString;
      this.pendingUiStartedAt = performance.now();
      emittedGenesThisCycle.add(votedResult.geneString);
      emittedAny = true;

      this.emit({
        type: 'SAPLING-FOUND',
        regionIndex: candidate.regionIndex,
        regionType: candidate.regionType,
        geneString: votedResult.geneString,
        confidence: votedResult.confidence,
        plantType: this.takePlantVote(candidate.regionIndex, votedResult.geneString)
      });
    }

    if (emittedAny) {
      this.setPipelineStage('ui-pending');
    } else if (confirmedAny) {
      this.setPipelineStage('duplicate');
    } else {
      this.setPipelineStage('vote-wait');
    }
  }

  /**
   * Keeps the last plant-name read for the HUD: the crop with the band outlined, so a miss
   * can be told apart at a glance (band off the title, title unreadable, unknown plant).
   */
  private recordPlantRead(
    crop: HTMLCanvasElement,
    row: RowGeometry,
    band: NameBand,
    x0: number,
    y0: number,
    regionType: ScannerRegionType,
    read: { text: string; plant: string | null; glyphCount: number }
  ): void {
    try {
      const debug = document.createElement('canvas');
      debug.width = crop.width;
      debug.height = crop.height;
      const ctx = debug.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(crop, 0, 0);
      const rect = plantNameRect(row, regionType, band);
      ctx.strokeStyle = read.plant ? '#22c55e' : '#ef4444';
      ctx.lineWidth = 1;
      ctx.strokeRect(rect.x0 - x0 + 0.5, rect.y0 - y0 + 0.5, rect.x1 - rect.x0, rect.y1 - rect.y0);
      this.lastPlantRead = {
        regionType,
        text: read.text,
        plant: read.plant,
        glyphs: read.glyphCount,
        imageUrl: debug.toDataURL('image/png')
      };
    } catch {
      // Diagnostics only.
    }
  }

  /**
   * Reads the plant name on each frame a row is read and counts the answers, so one frame
   * with the cursor over the title, a flicker or a misplaced band does not decide the plant.
   * At most a few reads per row; each is a small crop.
   */
  private votePlantType(candidate: ScanCandidate): void {
    const key = `${candidate.regionIndex}:${candidate.genes}`;
    let votes = this.plantVotes.get(key);
    if (!votes) {
      if (this.plantVotes.size > 32) this.plantVotes.clear();
      votes = new Map();
      this.plantVotes.set(key, votes);
    }
    let total = 0;
    for (const n of votes.values()) total += n;
    if (total >= 5) return;
    const plant = this.readPlantType(candidate);
    if (plant) votes.set(plant, (votes.get(plant) ?? 0) + 1);
  }

  /** The plant most reads of this row named, or undefined; forgets the row's votes. */
  private takePlantVote(regionIndex: number, genes: string): string | undefined {
    const key = `${regionIndex}:${genes}`;
    const votes = this.plantVotes.get(key);
    this.plantVotes.delete(key);
    let best: string | undefined;
    let bestCount = 0;
    for (const [plant, count] of votes ?? []) {
      if (count > bestCount) {
        best = plant;
        bestCount = count;
      }
    }
    if (best && votes && votes.size > 1) console.log('[Scanner] Plant name votes', Object.fromEntries(votes));
    return best;
  }

  /**
   * The plant the panel around a row names, from the live frame; any failure just means
   * "not detected". The bands are placed from the row's detected geometry.
   */
  private readPlantType(candidate: ScanCandidate): string | undefined {
    const row = candidate.rowGeometry;
    const frame = this.frameSource?.current();
    if (!row || !frame || !this.frameSource) return undefined;
    try {
      // The detected geometry first, then with the pitch a little off; each searches the
      // bands above the row (see PLANT_NAME_SEARCH).
      const attempts = [row, { ...row, pitch: row.pitch * 0.95 }, { ...row, pitch: row.pitch * 1.05 }];
      // One crop covers every band of every attempt.
      const rects = attempts.flatMap(a => PLANT_NAME_SEARCH[candidate.regionType].map(band => plantNameRect(a, candidate.regionType, band)));
      const x0 = Math.max(0, Math.floor(Math.min(...rects.map(r => r.x0))));
      const y0 = Math.max(0, Math.floor(Math.min(...rects.map(r => r.y0))));
      const x1 = Math.min(this.frameSource.width, Math.ceil(Math.max(...rects.map(r => r.x1))));
      const y1 = Math.min(this.frameSource.height, Math.ceil(Math.max(...rects.map(r => r.y1))));
      const w = x1 - x0;
      const h = y1 - y0;
      if (w < 8 || h < 6) return undefined;

      this.plantNameCanvas ??= document.createElement('canvas');
      const canvas = this.plantNameCanvas;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return undefined;
      ctx.drawImage(frame, x0, y0, w, h, 0, 0, w, h);
      const image = { data: ctx.getImageData(0, 0, w, h).data, width: w, height: h };

      let best: { attempt: RowGeometry; band: NameBand; read: ReturnType<typeof searchPlantTitle>['read'] } | null = null;
      for (const attempt of attempts) {
        const local = { x: attempt.x - x0, y: attempt.y - y0, pitch: attempt.pitch };
        const { read, band } = searchPlantTitle(image, local, candidate.regionType);
        if (!best || read.plant || read.text.length > best.read.text.length) best = { attempt, band, read };
        if (read.plant) break;
      }
      if (!best) return undefined;
      this.recordPlantRead(canvas, best.attempt, best.band, x0, y0, candidate.regionType, best.read);
      console.log(
        best.read.plant
          ? `[Scanner] Plant name: ${best.read.text} -> ${best.read.plant}`
          : `[Scanner] Plant name not read (saw "${best.read.text}", ${best.read.glyphCount} letters)`
      );
      return best.read.plant ?? undefined;
    } catch (error) {
      console.warn('[Scanner] Plant name read failed', error);
      return undefined;
    }
  }

  /**
   * Renders a zoomed surround preview with 6 gene slot guide stripes.
   * Includes surrounding context padding so users can easily align tooltips.
   */
  private renderPreview(
    rIdx: number,
    xPx: number,
    yPx: number,
    wPx: number,
    hPx: number,
    reg: ScannerRegion,
    /** The frame being scanned. Omitted by the nudge/scale callers, which take the latest. */
    sourceFrame?: CanvasImageSource
  ): void {
    const source = this.frameSource;
    const frame = sourceFrame ?? source?.current();
    if (!source || !frame || source.width === 0) return;

    const videoW = source.width;
    const videoH = source.height;

    // Surrounding context padding
    const padX = Math.round(wPx * 0.15);
    const padY = Math.round(hPx * 0.75);

    const srcX = Math.max(0, xPx - padX);
    const srcY = Math.max(0, yPx - padY);
    const srcW = Math.min(videoW - srcX, wPx + padX * 2);
    const srcH = Math.min(videoH - srcY, hPx + padY * 2);

    if (srcW <= 0 || srcH <= 0) return;

    // Target preview resolution for crisp high-DPI display without CPU lag
    const targetW = 360;
    const scale = targetW / srcW;
    const targetH = Math.round(srcH * scale);

    if (!this.previewCanvases[rIdx]) {
      this.previewCanvases[rIdx] = document.createElement('canvas');
    }
    const pCanvas = this.previewCanvases[rIdx];
    pCanvas.width = targetW;
    pCanvas.height = targetH;
    const pCtx = pCanvas.getContext('2d');
    if (!pCtx) return;

    // 1. Draw zoomed surrounding video area
    pCtx.imageSmoothingEnabled = false;
    pCtx.drawImage(frame, srcX, srcY, srcW, srcH, 0, 0, targetW, targetH);

    // 2. Compute local coordinates of the exact capture bounding box inside the preview
    const localBoxX = (xPx - srcX) * scale;
    const localBoxY = (yPx - srcY) * scale;
    const localBoxW = wPx * scale;
    const localBoxH = hPx * scale;

    // 3. Darken the outside surrounding context slightly for focus
    pCtx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    // Top
    pCtx.fillRect(0, 0, targetW, localBoxY);
    // Bottom
    pCtx.fillRect(0, localBoxY + localBoxH, targetW, targetH - (localBoxY + localBoxH));
    // Left
    pCtx.fillRect(0, localBoxY, localBoxX, localBoxH);
    // Right
    pCtx.fillRect(localBoxX + localBoxW, localBoxY, targetW - (localBoxX + localBoxW), localBoxH);

    // 4. Draw the 6 gene slot stripes (alternating white/shaded guide columns)
    const geneWPx = Math.round(wPx * reg.GENE_WIDTH_TO_WIDTH_RATIO);
    const totalGeneW = geneWPx * 6;
    const gapW = Math.max(0, (wPx - totalGeneW) / 5);

    for (let slot = 0; slot < 6; slot++) {
      const slotSrcX = xPx + slot * (geneWPx + gapW);
      const slotLocalX = (slotSrcX - srcX) * scale;
      const slotLocalW = geneWPx * scale;

      // Alternating stripe highlight
      pCtx.fillStyle = slot % 2 === 0 ? 'rgba(255, 255, 255, 0.28)' : 'rgba(200, 200, 200, 0.15)';
      pCtx.fillRect(slotLocalX, localBoxY, slotLocalW, localBoxH);

      // Slot divider border
      pCtx.strokeStyle = 'rgba(0, 229, 255, 0.6)';
      pCtx.lineWidth = 1;
      pCtx.strokeRect(slotLocalX, localBoxY, slotLocalW, localBoxH);

      // Slot number label above slot
      pCtx.fillStyle = '#00E5FF';
      pCtx.font = 'bold 9px monospace';
      pCtx.textAlign = 'center';
      pCtx.fillText(`${slot + 1}`, slotLocalX + slotLocalW / 2, Math.max(10, localBoxY - 3));
    }

    // 5. Draw the outer bounding box
    pCtx.strokeStyle = '#00E5FF';
    pCtx.lineWidth = 1.5;
    pCtx.strokeRect(localBoxX, localBoxY, localBoxW, localBoxH);

    this.emit({
      type: 'PREVIEW',
      regionIndex: rIdx,
      regionType: rIdx === 0 ? 'inventory' : 'planter',
      previewDataUrl: pCanvas.toDataURL('image/jpeg', 0.68)
    });
  }

  public getDiagnostics(): ScannerDiagnostics {
    const now = performance.now();
    const lastRead = this.getLastRead();
    return {
      fps: this.currentFps,
      tickGapMs: Math.round(this.lastTickGap * 10) / 10,
      videoFrameAgeMs: Math.round((this.lastVideoFrameTime > 0 ? now - this.lastVideoFrameTime : 0) * 10) / 10,
      videoFrameGapMs: Math.round(this.lastVideoFrameGap * 10) / 10,
      lastScanLatencyMs: Math.round(this.lastScanLatency * 10) / 10,
      lastOcrLatencyMs: Math.round(this.lastOcrLatency * 10) / 10,
      rowOcrLatencyMs: Math.round(this.lastRowOcrLatency * 10) / 10,
      slotOcrLatencyMs: Math.round(this.lastSlotOcrLatency * 10) / 10,
      pipelineStage: this.pipelineStage,
      pipelineStageAgeMs: Math.round((now - this.pipelineStageStartedAt) * 10) / 10,
      uiUpdateLatencyMs: Math.round((this.pendingUiStartedAt > 0 ? now - this.pendingUiStartedAt : this.lastUiUpdateLatency) * 10) / 10,
      pageVisibility: document.visibilityState,
      captureResolution: this.frameSource ? `${this.frameSource.width}x${this.frameSource.height}` : '0x0',
      confidence: Math.round(this.latestConfidence),
      totalScans: this.scanCount,
      acceptedPlants: this.acceptedCount,
      rejectedScans: this.rejectedCount,
      activeRegion: this.activeRegionType,
      inventoryActivity: this.activityScores[0] || 0,
      planterActivity: this.activityScores[1] || 0,
      isStarved: this.starvationDetector.getIsStarved(),
      starvationReason: this.starvationDetector.getStarvationReason(),
      frameSource: this.frameSource?.kind ?? 'none',
      layoutSource: lastRead?.layoutSource ?? 'none',
      resolvedSlots: lastRead?.resolvedCount ?? 0,
      lastPartialRead: lastRead?.partial ?? '',
      fallbackReads: this.fallbackReadCount,
      plantRead: this.lastPlantRead
    };
  }

  /** The most recent read from whichever region last produced one. */
  private getLastRead(): DesktopTemplateResult | null {
    const active = this.lastReads[1] ?? null;
    const inventory = this.lastReads[0] ?? null;
    if (!active) return inventory;
    if (!inventory) return active;
    return active.resolvedCount >= inventory.resolvedCount ? active : inventory;
  }

  public acknowledgeGeneHandled(geneString: string): void {
    if (geneString !== this.pendingUiGene || this.pendingUiStartedAt === 0) return;
    this.lastUiUpdateLatency = performance.now() - this.pendingUiStartedAt;
    this.pendingUiGene = '';
    this.pendingUiStartedAt = 0;
    this.setPipelineStage('accepted');
  }

  private setPipelineStage(stage: string): void {
    if (stage === this.pipelineStage) return;
    this.pipelineStage = stage;
    this.pipelineStageStartedAt = performance.now();
  }

  public moveRegion(regionIndex: number, dx: number, dy: number, videoW = 1920, videoH = 1080): void {
    const reg = this.regions[regionIndex];
    if (!reg) return;

    // Support both normalized delta (< 0.5) and pixel delta (>= 1)
    const actualVideoW = this.frameSource?.width || videoW;
    const actualVideoH = this.frameSource?.height || videoH;
    const dxNorm = Math.abs(dx) < 0.5 ? dx : dx / actualVideoW;
    const dyNorm = Math.abs(dy) < 0.5 ? dy : dy / actualVideoH;

    reg.TOP_LEFT_X = Math.max(0, Math.min(1 - reg.WIDTH, reg.TOP_LEFT_X + dxNorm));
    const normH = reg.WIDTH * reg.HEIGHT_TO_WIDTH_RATIO;
    reg.TOP_LEFT_Y = Math.max(0, Math.min(1 - normH, reg.TOP_LEFT_Y + dyNorm));

    this.saveRegions();

    // Trigger instant preview frame re-render
    if (this.frameSource && this.frameSource.width > 0) {
      const xPx = Math.round(actualVideoW * reg.TOP_LEFT_X);
      const yPx = Math.round(actualVideoH * reg.TOP_LEFT_Y);
      const wPx = Math.round(actualVideoW * reg.WIDTH);
      const hPx = Math.ceil(actualVideoH * normH);
      this.renderPreview(regionIndex, xPx, yPx, wPx, hPx, reg);
    }
  }

  public scaleRegion(regionIndex: number, dw: number, videoW = 1920): void {
    const reg = this.regions[regionIndex];
    if (!reg) return;

    const actualVideoW = this.frameSource?.width || videoW;
    const actualVideoH = this.frameSource?.height || 1080;
    const dwNorm = Math.abs(dw) < 0.5 ? dw : dw / actualVideoW;
    const newWidth = Math.max(0.02, Math.min(0.5, reg.WIDTH + dwNorm));
    const normH = newWidth * reg.HEIGHT_TO_WIDTH_RATIO;

    if (reg.TOP_LEFT_X + newWidth <= 1.0 && reg.TOP_LEFT_Y + normH <= 1.0) {
      reg.WIDTH = newWidth;
    }

    this.saveRegions();

    // Trigger instant preview frame re-render
    if (this.frameSource && this.frameSource.width > 0) {
      const xPx = Math.round(actualVideoW * reg.TOP_LEFT_X);
      const yPx = Math.round(actualVideoH * reg.TOP_LEFT_Y);
      const wPx = Math.round(actualVideoW * reg.WIDTH);
      const hPx = Math.ceil(actualVideoH * normH);
      this.renderPreview(regionIndex, xPx, yPx, wPx, hPx, reg);
    }
  }

  public adjustHeightRatio(regionIndex: number, dRatio: number): void {
    const reg = this.regions[regionIndex];
    if (!reg) return;
    reg.HEIGHT_TO_WIDTH_RATIO = Math.max(0.05, Math.min(0.5, reg.HEIGHT_TO_WIDTH_RATIO + dRatio));
  }

  public adjustGeneWidthRatio(regionIndex: number, dRatio: number): void {
    const reg = this.regions[regionIndex];
    if (!reg) return;
    reg.GENE_WIDTH_TO_WIDTH_RATIO = Math.max(0.02, Math.min(0.25, reg.GENE_WIDTH_TO_WIDTH_RATIO + dRatio));
  }

  public setRegions(newRegions: ScannerRegion[]): void {
    this.regions = newRegions.map(r => ({ ...r }));
    this.saveRegions();

    // Trigger instant preview frame re-render for both regions
    if (this.frameSource && this.frameSource.width > 0) {
      const videoW = this.frameSource.width;
      const videoH = this.frameSource.height;
      for (let rIdx = 0; rIdx < this.regions.length; rIdx++) {
        const reg = this.regions[rIdx];
        const xPx = Math.round(videoW * reg.TOP_LEFT_X);
        const yPx = Math.round(videoH * reg.TOP_LEFT_Y);
        const wPx = Math.round(videoW * reg.WIDTH);
        const normH = reg.WIDTH * reg.HEIGHT_TO_WIDTH_RATIO;
        const hPx = Math.ceil(videoH * normH);
        this.renderPreview(rIdx, xPx, yPx, wPx, hPx, reg);
      }
    }
  }

  public saveRegions(): void {
    StorageService.saveScannerRegions(this.regions);
  }

  public resetRegions(): ScannerRegion[] {
    this.regions = StorageService.resetScannerRegions();
    if (this.frameSource && this.frameSource.width > 0) {
      this.setRegions(this.regions);
    }
    return this.regions;
  }

  public getRegions(): ScannerRegion[] {
    return this.regions;
  }

  /** True once the capture has delivered a frame the scanner can read. */
  public isCaptureReady(): boolean {
    return !!this.frameSource && this.frameSource.width > 0 && this.frameSource.current() !== null;
  }

  /**
   * Runs 1-Click Auto Calibration using the live video stream from screen capture.
   */
  public async autoCalibrate(preferredRegionIndex?: number): Promise<AutoCalibrateResult> {
    if (!this.isCaptureReady()) {
      return {
        success: false,
        regionIndex: preferredRegionIndex ?? 0,
        message: 'Screen capture is not active. Please start the scanner first.'
      };
    }

    const res = await AutoCalibrator.calibrateFromFrame(
      this.frameSource!.current(),
      this.frameSource!.width,
      this.frameSource!.height,
      preferredRegionIndex,
      this.recognizer
    );

    if (res.success && res.region) {
      const updatedRegions = [...this.regions];
      updatedRegions[res.regionIndex] = res.region;
      this.setRegions(updatedRegions);
    }

    return res;
  }

  public stop(): void {
    this.isScanning = false;
    this.isInitializing = false;
    this.starvationDetector.reset();
    this.votingService.reset();
    this.deduplicator.reset();
    this.changeDetector.reset();
    this.stabilityDetector.reset();
    this.lastReads = {};
    this.slotOcrAttempts = {};
    this.lastScannedFrameCount = -1;
    this.slotOcrInFlight = false;
    releaseRasterCanvases();

    if (this.frameSource) {
      this.frameSource.stop();
      this.frameSource = null;
    }

    if (this.tickerWorker) {
      try {
        this.tickerWorker.postMessage('STOP');
        this.tickerWorker.terminate();
      } catch {
        // ignore
      }
      this.tickerWorker = null;
    }

    if (this.scanTimerId) {
      clearTimeout(this.scanTimerId);
      this.scanTimerId = null;
    }

    if (this.mediaStream) {
      for (const track of this.mediaStream.getTracks()) {
        track.stop();
      }
      this.mediaStream = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
      if (this.videoElement.parentNode) {
        this.videoElement.parentNode.removeChild(this.videoElement);
      }
      this.videoElement = null;
    }

    this.emit({ type: 'STOPPED' });
  }

  public async terminateAll(): Promise<void> {
    this.stop();
    await this.recognizer.terminate();
  }
}
